/**
 * widgets/notification-center/ui/NotificationDropdown — Notification dropdown panel.
 *
 * Shows recent notifications (up to 5) with per-item mark-read and a
 * "mark all read" action. Includes loading, empty (EmptyState), and error
 * (with retry) states. A "View all" link navigates to the full notifications page.
 *
 * Also contains the email opt-in toggle so users can change their email
 * notification preference without navigating to Settings (AC4).
 * The toggle uses useNotificationPreferencesQuery / useUpdateEmailOptInMutation
 * from entities/notification, which share the same TanStack Query cache key
 * as features/account-settings, so toggling here is immediately reflected in
 * the Settings page preferences form (single source of truth).
 *
 * FSD: widgets may import from entities and shared only.
 */

import { useTranslation } from 'react-i18next';
import { Bell, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { EmptyState } from '@/shared/ui/empty-state/EmptyState';
import { useToast } from '@/shared/ui/use-toast';
import {
  useNotificationsQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  NotificationItem,
  useNotificationPreferencesQuery,
  useUpdateEmailOptInMutation,
} from '@/entities/notification';

/** Maximum number of notifications to show in the dropdown. */
const DROPDOWN_LIMIT = 5;

interface NotificationDropdownProps {
  /** Called when the user navigates to the full notifications page. */
  onViewAll: () => void;
}

export function NotificationDropdown({ onViewAll }: NotificationDropdownProps) {
  const { t } = useTranslation('notifications');
  const tSettings = useTranslation('settings').t;
  const { toast } = useToast();

  const { data: notifications, isLoading, isError, refetch } = useNotificationsQuery();
  const markReadMutation = useMarkReadMutation();
  const markAllReadMutation = useMarkAllReadMutation();

  const { data: prefs } = useNotificationPreferencesQuery();
  const updateOptInMutation = useUpdateEmailOptInMutation();

  const recentNotifications = (notifications ?? []).slice(0, DROPDOWN_LIMIT);
  const hasUnread = (notifications ?? []).some((n) => !n.is_read);
  const emailOptIn = prefs?.email_opt_in ?? true;

  function handleMarkRead(id: string) {
    markReadMutation.mutate(id);
  }

  function handleMarkAllRead() {
    markAllReadMutation.mutate();
  }

  function handleOptInToggle(e: React.ChangeEvent<HTMLInputElement>) {
    updateOptInMutation.mutate(
      { email_opt_in: e.target.checked },
      {
        onError: () => {
          toast({ title: t('optInError'), variant: 'destructive' });
        },
      },
    );
  }

  return (
    <div
      data-testid="notification-dropdown"
      className="flex flex-col"
      role="region"
      aria-label={t('title')}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h2 className="text-sm font-semibold">{t('title')}</h2>
        {hasUnread && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={markAllReadMutation.isPending}
            data-testid="mark-all-read-btn"
            className="h-auto min-h-[44px] px-2 text-xs text-primary hover:text-primary/80"
          >
            {t('markAllRead')}
          </Button>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-2 overflow-y-auto p-2" style={{ maxHeight: '20rem' }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="notification-loading">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="ml-2 text-sm text-muted-foreground">{t('loading')}</span>
          </div>
        ) : isError ? (
          <div
            className="flex flex-col items-center gap-2 px-4 py-6 text-center"
            role="alert"
            data-testid="notification-error"
          >
            <p className="text-sm text-destructive">{t('error')}</p>
            <button
              type="button"
              className="text-sm text-muted-foreground underline hover:text-foreground"
              onClick={() => void refetch()}
            >
              {t('retry')}
            </button>
          </div>
        ) : recentNotifications.length === 0 ? (
          <EmptyState
            icon={<Bell className="h-8 w-8" />}
            title={t('empty')}
            description={t('emptyDescription')}
            data-testid="notification-empty"
          />
        ) : (
          recentNotifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
              isMarkingRead={
                markReadMutation.isPending &&
                markReadMutation.variables === notification.id
              }
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewAll}
          data-testid="view-all-btn"
          className="w-full text-xs"
        >
          {t('viewAll')}
        </Button>
      </div>

      {/* Email opt-in toggle (AC4: synced with Settings page via shared cache) */}
      <div className="border-t px-4 py-2">
        <label
          className="flex min-h-[44px] cursor-pointer items-center gap-2"
          data-testid="email-opt-in-toggle"
        >
          <input
            type="checkbox"
            checked={emailOptIn}
            onChange={handleOptInToggle}
            disabled={updateOptInMutation.isPending}
            className="h-4 w-4 shrink-0 rounded border-border"
            aria-label={tSettings('preferences.notifications.label')}
          />
          <span className="text-xs text-muted-foreground">
            {tSettings('preferences.notifications.label')}
          </span>
        </label>
      </div>
    </div>
  );
}
